import { useState } from 'react';
import { Button, LoadingSpinner } from '~/components';
import { grantEmployerCredit, listEmployerCredits, revokeEmployerCredit } from './api';
import { formatCents, formatDate } from './format';
import { EmployerCredit } from './types';

export const AdminCreditsPage = () => {
  const [employerId, setEmployerId] = useState('');
  const [displayedEmployerId, setDisplayedEmployerId] = useState<number | null>(null);
  const [credits, setCredits] = useState<EmployerCredit[]>([]);
  const [amount, setAmount] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [granting, setGranting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedEmployerId = Number(employerId);

  const loadCredits = async (targetEmployerId = parsedEmployerId) => {
    if (!Number.isFinite(targetEmployerId) || targetEmployerId <= 0) {
      setCredits([]);
      setDisplayedEmployerId(null);
      setError('Enter a valid employer ID.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const fetchedCredits = await listEmployerCredits(targetEmployerId);
      setCredits(fetchedCredits);
      setDisplayedEmployerId(targetEmployerId);
    } catch {
      setCredits([]);
      setDisplayedEmployerId(null);
      setError('Unable to load credits for this employer.');
    } finally {
      setLoading(false);
    }
  };

  const handleGrant = async () => {
    if (granting) return;
    setError(null);
    const amountNumber = Number(amount);
    const expiresDate = new Date(`${expiresAt}T23:59:59.999Z`);
    if (
      !Number.isFinite(parsedEmployerId) ||
      parsedEmployerId <= 0 ||
      !Number.isFinite(amountNumber) ||
      amountNumber <= 0 ||
      !expiresAt ||
      Number.isNaN(expiresDate.getTime())
    ) {
      setError('Enter a valid employer ID, amount, and expiration date.');
      return;
    }
    const amountCents = Math.round(amountNumber * 100);
    setGranting(true);
    try {
      await grantEmployerCredit(parsedEmployerId, {
        amountCents,
        expiresAt: expiresDate.toISOString(),
        notes: notes || undefined,
      });
      setAmount('');
      setExpiresAt('');
      setNotes('');
      await loadCredits(parsedEmployerId);
    } catch {
      setError('Unable to grant credit.');
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (creditId: number) => {
    try {
      await revokeEmployerCredit(creditId);
      if (displayedEmployerId !== null) {
        await loadCredits(displayedEmployerId);
      }
    } catch {
      setError('Unable to revoke credit.');
    }
  };

  return (
    <section className="p-5 sm:p-8 bg-white rounded-xl shadow-sm border border-grey-900 w-full mx-auto">
      <header className="pb-5 sm:pb-6 mb-5 sm:mb-6 border-b border-grey-900">
        <h2 className="sub-heading-sb text-grey-100 mb-2">Billing Credits</h2>
        <p className="body-text text-grey-400">
          Grant and revoke promotional credits for employer accounts.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-[160px_auto_160px_200px_1fr_auto] mb-6 items-end">
        <label className="body-text">
          Employer ID
          <input
            className="mt-1 w-full rounded-md border border-grey-900 px-3 py-2"
            value={employerId}
            onChange={(event) => setEmployerId(event.target.value)}
            inputMode="numeric"
          />
        </label>
        <Button type="button" colorVariant="secondary" onClick={() => void loadCredits()}>
          Load
        </Button>
        <label className="body-text">
          Amount
          <input
            className="mt-1 w-full rounded-md border border-grey-900 px-3 py-2"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            inputMode="decimal"
          />
        </label>
        <label className="body-text">
          Expires
          <input
            className="mt-1 w-full rounded-md border border-grey-900 px-3 py-2"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
            type="date"
          />
        </label>
        <label className="body-text">
          Notes
          <input
            className="mt-1 w-full rounded-md border border-grey-900 px-3 py-2"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </label>
        <Button type="button" onClick={handleGrant} disabled={granting}>
          Grant
        </Button>
      </div>
      {error && <p className="body-text text-error mb-4">{error}</p>}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-x-auto border border-grey-900 rounded-lg">
          <table className="min-w-full divide-y divide-grey-900">
            <thead className="bg-grey-950">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">ID</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">
                  Initial
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-grey-100">
                  Remaining
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Expires</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-grey-100">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-900">
              {credits.map((credit) => (
                <tr key={credit.id}>
                  <td className="px-4 py-3 body-text">{credit.id}</td>
                  <td className="px-4 py-3 body-text text-right">
                    {formatCents(credit.initialAmountCents)}
                  </td>
                  <td className="px-4 py-3 body-text text-right">
                    {formatCents(credit.remainingAmountCents)}
                  </td>
                  <td className="px-4 py-3 body-text">{formatDate(credit.expiresAt)}</td>
                  <td className="px-4 py-3 body-text">{credit.revokedAt ? 'Revoked' : 'Active'}</td>
                  <td className="px-4 py-3 body-text">
                    {!credit.revokedAt && (
                      <Button
                        type="button"
                        colorVariant="secondary"
                        onClick={() => handleRevoke(credit.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {credits.length === 0 && (
                <tr>
                  <td className="px-4 py-6 body-text text-grey-400" colSpan={6}>
                    Enter an employer ID to review credits.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
